<template>
  <div v-if="step === 'form'">
    <div class="px-[24px] py-[32px] font-[Inter]">
      <h4 class="text-text-display-lg font-black tracking-[-0.03em] mb-[24px] text-brown-800">
        Just a little more...
      </h4>

      <div class="mb-[64px] sm:mb-[48px] flex flex-col gap-6">
        <div>
          <label :class="labelClass">Mobile Number</label>
          <!--
            Still vue-tel-input: the design system has no inline country-code
            prefix on Input or Verification, so there is nothing to swap the
            picker for yet (DS_GAPS.md, section A).
          -->
          <vue-tel-input
            class="bg-white border-[1px] border-solid border-grey-20 rounded-[6px] min-h-[52px]"
            v-model="phone"
            :default="register.showMobileNumberIfVerified"
            :dropdown-options="{
              showDialCodeInList: true,
              showDialCodeInSelection: true,
              showFlags: true,
              showSearchBox: true,
            }"
            default-country="SG"
            type="tel"
            :preferred-countries="['SG']"
            :input-options="{
              placeholder: '',
              type: 'tel',
              autofocus: true,
            }"
            @validate="validateNumber"
            @blur="() => $store.dispatch('profile/blurField', 'phone')"
          />
          <p v-if="errorMessages.phone" class="text-status-error text-text-body mt-1">
            {{ errorMessages.phone[0] }}
          </p>
        </div>

        <div>
          <label :class="labelClass" for="email">Email Address</label>
          <DsInput
            id="email"
            name="email"
            type="email"
            v-model="data.email"
            placeholder="@example.com"
            :error="shownEmailError"
            :help-text="shownEmailError ? '' : 'You can use this email to login next time'"
            @blur="emailTouched = true"
          />
        </div>
      </div>

      <div :style="hasFocusedField ? `padding-bottom: ${keyboardInsetCss}` : ''">
        <DsButton
          variant="secondary"
          shape="pill"
          size="56"
          class="w-full !text-text-body-md"
          :disabled="isButtonDisabled || hasPhoneError"
          :loading="formloading"
          @click="handleClick"
        >
          {{ formloading ? 'Saving...' : 'Save and Next' }}
        </DsButton>
      </div>
    </div>
  </div>
  <div v-else class="register-otp">
    <otp
      v-if="pendingOtp.length > 0 && step === 'emailOtp'"
      is-otp-type="emailOtp"
      :email="data.email"
      :is-onboarding="true"
      :email-validated="register.isEmailVerified"
      class="pt-[24px] font-[Inter]"
      @nextButtonHandler="finishVerifyEmailOtp"
    ></otp>

    <otp
      v-if="pendingOtp.length > 0 && step === 'phoneOtp'"
      is-otp-type="mobileOtp"
      :mobile-number="trimmedPhone"
      :country-code="countryCode"
      class="pt-[24px] font-[Inter]"
      @nextButtonHandler="finishVerifyMobileOtp"
    ></otp>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import axios from 'axios';
import { Button as DsButton, Input as DsInput } from '@team-hogc/hogc-one-design-system';
import Otp from '@/components/common/Otp.vue';
import { validateEmail } from '@/utils/registration-validation';
import keyboardFieldIntoView from '@/mixins/keyboard-field-into-view';
import { isLinkRequired, LINK_REQUIRED_MESSAGE } from '@/utils/member-split/link-required';

export default {
  mixins: [keyboardFieldIntoView],
  components: { DsButton, DsInput, Otp },
  data() {
    return {
      agree: false,
      data: { email: '' },
      phone: '',
      countryCode: '',
      showMobileOtp: false,
      showEmailOtp: false,
      isEmailVerified: false,
      pendingOtp: [],
      step: 'form',
      emailTouched: false,
      labelClass: 'block text-base text-primary font-inter font-medium mb-1 leading-5',
    };
  },
  computed: {
    ...mapState('profile', {
      formloading: state => state.loading,
      register: state => state.register,
      errorMessages: state => state.errorMessages,
    }),
    ...mapState('user', ['token']),
    emailError() {
      return validateEmail(this.data.email);
    },
    shownEmailError() {
      return this.emailTouched ? this.emailError || '' : '';
    },
    isButtonDisabled() {
      return Boolean(this.emailError);
    },
    hasPhoneError() {
      return Boolean(this.errorMessages.phone);
    },
    trimmedPhone() {
      return this.phone.replace(/\s+/g, '');
    },
    originalPhone() {
      return this.register.mobileNumber;
    },
    originalEmail() {
      return this.register.email;
    },
  },
  async mounted() {
    this.startKeyboardOverlapTracking();

    await this.$store.dispatch('profile/fetchProfile');
    if (this.register.isEmailVerified) {
      this.data.email = this.register.email ?? '';
    }
    if (this.register.isMobileNumberVerified) {
      this.phone = this.register.mobileNumber;
    }
  },
  methods: {
    inputRegisterForm(key, value) {
      this.$store.dispatch('profile/inputRegisterForm', { key, value });
    },
    async validateNumber(phoneObject) {
      await this.$store.dispatch('profile/validateContactNumber', phoneObject.valid);
      if (phoneObject.valid) {
        this.inputRegisterForm('phone', phoneObject.nationalNumber);
        this.inputRegisterForm('countryCode', phoneObject.countryCallingCode);
        this.countryCode = phoneObject.countryCallingCode;
      }
    },
    handleClick() {
      this.emailTouched = true;
      if (this.isButtonDisabled || this.hasPhoneError) return;

      this.pendingOtp = [];

      const phoneChanged =
        (this.trimmedPhone || '') !== String(this.originalPhone || '').replace(/\s+/g, '');

      const emailChanged = this.data.email !== this.originalEmail;

      if (!this.register.isMobileNumberVerified || phoneChanged) {
        this.pendingOtp.push('phoneOtp');
      }

      if (!this.register.isEmailVerified || emailChanged) {
        this.pendingOtp.push('emailOtp');
      }

      // move into the first pending step, or finish immediately
      if (this.pendingOtp.length > 0) {
        this.step = this.pendingOtp[0];
      } else {
        this.finishBoth();
      }
    },
    back() {
      this.pendingOtp = [];
      this.step = 'form';
      this.inputRegisterForm('stage', 1);
    },
    async finishVerifyMobileOtp() {
      this.register.isMobileNumberVerified = true;
      this.register.mobileNumber = this.trimmedPhone;
      // remove the step we just completed
      if (this.pendingOtp[0] === 'phoneOtp') this.pendingOtp.shift();

      if (this.pendingOtp.length > 0) {
        this.step = this.pendingOtp[0];
      } else {
        this.finishBoth();
      }
    },
    async finishVerifyEmailOtp() {
      this.register.isEmailVerified = true;
      this.register.email = this.data.email;
      if (this.pendingOtp[0] === 'emailOtp') this.pendingOtp.shift();

      if (this.pendingOtp.length > 0) {
        this.step = this.pendingOtp[0];
      } else {
        this.finishBoth();
      }
    },
    async finishBoth() {
      const payload = {
        countryCode: this.countryCode,
        mobileNumber: this.trimmedPhone,
        email: this.data.email,
      };

      // Saved first, then moved on: the step used to advance before the PUT, so a
      // failed save left the person past stage 2 with nothing stored.
      try {
        await axios.put(process.env.VUE_APP_API_URL + `/v2/profile/stage-2`, payload, {
          headers: { Authorization: `Bearer ${this.token}` },
        });
      } catch (e) {
        console.error('Stage 2 save error:', e);
        this.step = 'form';
        this.$store.dispatch(
          'toast/error',
          isLinkRequired(e) ? LINK_REQUIRED_MESSAGE : 'Something went wrong saving your details',
        );
        return;
      }
      this.step = 'done';
      this.inputRegisterForm('stage', 2);
      // Save onboarding status to localStorage after successful stage-2 completion
      this.$store.dispatch('profile/updateOnboardingStatusAfterStage', 2);
    },
  },
};
</script>

<style scoped>
:deep(.vue-tel-input .vti__input) {
  flex: 1 1 auto;
  width: 100%;
  border: none;
  box-shadow: none;
}
</style>
